import type { Socket } from 'socket.io-client'
import {
  SOCKET_EVENTS,
  parseAvatarConfig,
  type AvatarConfig,
  type GlobalChatHistoryPayload,
  type GlobalChatMessagePayload,
  type GlobalOnlinePayload,
  type MatchmakingCallPayload,
  type AccountRewardPayload,
  type PlayerMailPayload,
  type DirectMessagePayload,
  type FriendDmErrorPayload,
  type FriendInvitePayload,
  type FriendInviteDeclinedPayload,
  type MatchmakingClaimResultPayload,
  type MatchmakingQueuePayload,
  type TournamentCallPayload,
  type TournamentPublic,
} from '@shared/index'
import { createAppSocket } from './createAppSocket'
import { publishClientRename, readAccountRename } from '../../account/liveRename'
import { releasePresenceHold } from './presenceHold'
import { useSiteStore } from '../../stores/site'
import {
  applyRankedCalls,
  bindRankedClaim,
  readRankedCalls,
  settleRankedClaim,
} from './rankedCall'

export type GlobalChatHandlers = {
  onMessage: (message: GlobalChatMessagePayload) => void
  onHistory: (messages: GlobalChatMessagePayload[]) => void
  onOnline: (count: number, users: string[]) => void
  onQueue: (count: number, closed: boolean, casual: number, event: number) => void
  onReward: (payload: AccountRewardPayload) => void
  onMail: (payload: PlayerMailPayload) => void
  onTournament: (payload: TournamentPublic) => void
  onTournamentCall: (payload: TournamentCallPayload) => void
  onFriendUpdated: () => void
  onDm: (payload: DirectMessagePayload) => void
  onDmError: (payload: FriendDmErrorPayload) => void
  onInvite: (payload: FriendInvitePayload) => void
  onInviteDeclined: (payload: FriendInviteDeclinedPayload) => void
}

/** How often a visible, in-use tab tells the server it is still here. */
const PULSE_MS = 45_000
/** Background tab: close locally if the server sweep has not already. */
const HIDDEN_MS = 10 * 60 * 1000
/** Focused tab with no pointer, key, or touch. */
const IDLE_MS = 45 * 60 * 1000
const WATCH_MS = 15_000

/**
 * Dedicated socket for site-wide chat. Separate from the PvP duel socket
 * so the duel connection never joins the global room.
 *
 * The socket stays up only while this tab is visible and recently used.
 * A frozen background tab keeps answering Socket.IO pings; the presence
 * pulse does not, so the server can drop it.
 */
export class GlobalChatClient {
  private readonly socket: Socket
  private username: string
  private avatar: AvatarConfig
  private readonly clientId: string
  private readonly handlers: GlobalChatHandlers
  private disposed = false
  /** Closed on purpose. Do not reconnect until the player comes back. */
  private asleep = false
  private hiddenSince: number | null = null
  private lastInput = Date.now()
  private lastPulse = 0
  private readonly timer: number

  constructor(opts: {
    username: string
    clientId: string
    avatar?: AvatarConfig
    handlers: GlobalChatHandlers
  }) {
    this.username = opts.username
    this.avatar = parseAvatarConfig(opts.avatar)
    this.clientId = opts.clientId
    this.handlers = opts.handlers

    this.socket = createAppSocket()
    if (document.visibilityState === 'hidden') {
      this.hiddenSince = Date.now()
      this.socket.io.reconnection(false)
    }

    this.socket.on('connect', () => {
      if (this.disposed) return
      if (!this.awake()) {
        this.socket.io.reconnection(false)
        this.socket.disconnect()
        return
      }
      this.join()
      this.pulseNow()
    })
    this.socket.on(SOCKET_EVENTS.PRESENCE_SLEEP, () => {
      if (this.awake()) return
      this.asleep = true
      this.socket.io.reconnection(false)
    })
    this.socket.on('disconnect', (reason: string) => {
      if (this.disposed || this.asleep || reason !== 'io server disconnect') return
      if (!this.awake()) {
        this.asleep = true
        this.socket.io.reconnection(false)
        return
      }
      this.socket.connect()
    })
    this.socket.on(
      SOCKET_EVENTS.GLOBAL_MESSAGE,
      (message: GlobalChatMessagePayload) => {
        this.handlers.onMessage(message)
      },
    )
    this.socket.on(
      SOCKET_EVENTS.GLOBAL_HISTORY,
      (payload: GlobalChatHistoryPayload) => {
        this.handlers.onHistory(payload?.messages ?? [])
      },
    )
    this.socket.on(
      SOCKET_EVENTS.GLOBAL_ONLINE,
      (payload: GlobalOnlinePayload) => {
        const count = payload?.count
        if (typeof count !== 'number' || count < 0) return
        const users = Array.isArray(payload?.users)
          ? payload.users.filter((name): name is string => typeof name === 'string')
          : []
        this.handlers.onOnline(count, users)
      },
    )
    this.socket.on(
      SOCKET_EVENTS.MATCHMAKING_QUEUE,
      (payload: MatchmakingQueuePayload) => {
        const count = payload?.count
        if (typeof count === 'number' && count >= 0) {
          const casual =
            typeof payload.casual === 'number' && payload.casual >= 0
              ? payload.casual
              : 0
          const event =
            typeof payload.event === 'number' && payload.event >= 0
              ? payload.event
              : 0
          this.handlers.onQueue(count, payload.closed === true, casual, event)
        }
      },
    )
    this.socket.on(
      SOCKET_EVENTS.MATCHMAKING_CALL,
      (payload: MatchmakingCallPayload) => {
        applyRankedCalls(readRankedCalls(payload))
      },
    )
    this.socket.on(SOCKET_EVENTS.ACCOUNT_REWARD, (payload: AccountRewardPayload) => {
      this.handlers.onReward(payload)
    })
    this.socket.on(SOCKET_EVENTS.ACCOUNT_MAIL, (payload: PlayerMailPayload) => {
      this.handlers.onMail(payload)
    })
    this.socket.on(SOCKET_EVENTS.TOURNAMENT_STATE, (payload: TournamentPublic) => {
      this.handlers.onTournament(payload)
    })
    this.socket.on(SOCKET_EVENTS.TOURNAMENT_CALL, (payload: TournamentCallPayload) => {
      if (!payload || typeof payload.matchId !== 'string' || typeof payload.opponent !== 'string') {
        return
      }
      this.handlers.onTournamentCall(payload)
    })
    this.socket.on(SOCKET_EVENTS.FRIEND_UPDATED, () => {
      this.handlers.onFriendUpdated()
    })
    this.socket.on(SOCKET_EVENTS.FRIEND_DM, (payload: DirectMessagePayload) => {
      if (!payload || typeof payload.id !== 'string') return
      this.handlers.onDm(payload)
    })
    this.socket.on(SOCKET_EVENTS.FRIEND_DM_ERROR, (payload: FriendDmErrorPayload) => {
      if (!payload || typeof payload.code !== 'string') return
      this.handlers.onDmError(payload)
    })
    this.socket.on(SOCKET_EVENTS.FRIEND_INVITE, (payload: FriendInvitePayload) => {
      if (!payload || typeof payload.username !== 'string' || typeof payload.code !== 'string') {
        return
      }
      this.handlers.onInvite(payload)
    })
    this.socket.on(
      SOCKET_EVENTS.FRIEND_INVITE_DECLINED,
      (payload: FriendInviteDeclinedPayload) => {
        if (!payload || typeof payload.username !== 'string') return
        this.handlers.onInviteDeclined(payload)
      },
    )
    this.socket.on(
      SOCKET_EVENTS.SITE_STATUS,
      (payload: {
        maintenance?: 'off' | 'closed' | 'full'
        eventQueue?: unknown
      }) => {
        useSiteStore().applyPush(payload ?? {})
      },
    )
    this.socket.on(SOCKET_EVENTS.ACCOUNT_RENAME, (payload: unknown) => {
      const swap = readAccountRename(payload)
      if (!swap) return
      publishClientRename(swap.previous, swap.username)
    })
    this.socket.on(
      SOCKET_EVENTS.MATCHMAKING_CLAIM_RESULT,
      (payload: MatchmakingClaimResultPayload) => {
        const callId = payload?.callId
        if (typeof callId !== 'string') return
        settleRankedClaim(callId, payload?.ok === true)
      },
    )
    bindRankedClaim({
      send: (id) => {
        this.socket.emit(SOCKET_EVENTS.MATCHMAKING_CLAIM, { callId: id })
      },
      cancel: (id) => {
        this.socket.emit(SOCKET_EVENTS.MATCHMAKING_CLAIM_CANCEL, { callId: id })
      },
    })

    document.addEventListener('visibilitychange', this.onVisibility)
    window.addEventListener('pointerdown', this.onActivity)
    window.addEventListener('pointermove', this.onActivity)
    window.addEventListener('keydown', this.onActivity)
    window.addEventListener('touchstart', this.onActivity)
    this.timer = window.setInterval(this.watch, WATCH_MS)
  }

  /**
   * Sends a line of chat. The server uses the nick from join/nick, not this text.
   */
  send(text: string): void {
    this.socket.emit(SOCKET_EVENTS.GLOBAL_SEND, { text })
  }

  sendDm(username: string, text: string): void {
    this.socket.emit(SOCKET_EVENTS.FRIEND_DM, { username, text })
  }

  declineInvite(username: string): void {
    this.socket.emit(SOCKET_EVENTS.FRIEND_INVITE_DECLINE, { username })
  }

  /**
   * Updates the display name used on later messages.
   */
  setUsername(username: string): void {
    this.username = username
    this.socket.emit(SOCKET_EVENTS.GLOBAL_NICK, {
      username,
      avatar: this.avatar,
    })
  }

  /**
   * Updates the paper-doll look used on later messages.
   */
  setAvatar(raw: unknown): void {
    this.avatar = parseAvatarConfig(raw)
    this.socket.emit(SOCKET_EVENTS.GLOBAL_NICK, {
      username: this.username,
      avatar: this.avatar,
    })
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    window.clearInterval(this.timer)
    document.removeEventListener('visibilitychange', this.onVisibility)
    window.removeEventListener('pointerdown', this.onActivity)
    window.removeEventListener('pointermove', this.onActivity)
    window.removeEventListener('keydown', this.onActivity)
    window.removeEventListener('touchstart', this.onActivity)
    bindRankedClaim(null)
    releasePresenceHold(this.socket)
    this.socket.io.reconnection(false)
    this.socket.removeAllListeners()
    this.socket.disconnect()
  }

  private readonly onActivity = (): void => {
    if (this.disposed) return
    this.lastInput = Date.now()
    if (this.asleep && document.visibilityState === 'visible') this.wake()
  }

  private readonly onVisibility = (): void => {
    if (this.disposed) return
    if (document.visibilityState === 'hidden') {
      if (this.hiddenSince === null) this.hiddenSince = Date.now()
      this.socket.io.reconnection(false)
      return
    }
    this.hiddenSince = null
    this.lastInput = Date.now()
    this.wake()
  }

  private readonly watch = (): void => {
    if (this.disposed || this.asleep) return
    const now = Date.now()
    if (document.visibilityState === 'hidden') {
      if (this.hiddenSince === null) this.hiddenSince = now
      this.socket.io.reconnection(false)
      if (now - this.hiddenSince >= HIDDEN_MS) this.sleep()
      return
    }
    this.hiddenSince = null
    if (now - this.lastInput >= IDLE_MS) {
      this.sleep()
      return
    }
    this.socket.io.reconnection(true)
    if (!this.socket.connected) {
      this.socket.connect()
      return
    }
    this.pulseNow()
  }

  /** Visible tab that was used inside the idle window. */
  private awake(): boolean {
    if (this.disposed || this.asleep) return false
    if (document.visibilityState !== 'visible') return false
    return Date.now() - this.lastInput < IDLE_MS
  }

  private wake(): void {
    if (this.disposed) return
    this.asleep = false
    this.socket.io.reconnection(true)
    if (!this.socket.connected) this.socket.connect()
    else this.pulseNow()
  }

  private sleep(): void {
    if (this.disposed || this.asleep) return
    this.asleep = true
    this.socket.io.reconnection(false)
    if (this.socket.connected) this.socket.disconnect()
  }

  private pulseNow(): void {
    if (!this.socket.connected || !this.awake()) return
    const now = Date.now()
    if (now - this.lastPulse < PULSE_MS) return
    this.lastPulse = now
    this.socket.emit(SOCKET_EVENTS.PRESENCE_PULSE)
  }

  private join(): void {
    this.socket.emit(SOCKET_EVENTS.GLOBAL_JOIN, {
      username: this.username,
      clientId: this.clientId,
      avatar: this.avatar,
    })
  }
}
